import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "../contexts/AuthContext";
import axios from "axios";
import { Link } from "react-router-dom";
import { Zap, Clock, Lock, ChevronRight, Trophy, Shield, Check, Calendar, Users, History } from "lucide-react";
import useCelebration from "../hooks/useCelebration";
import CelebrationOverlay from "../components/CelebrationOverlay";
import SponsorBanner from "../components/SponsorBanner";
import { getDisplayName, getDisplayShortName } from "../utils/teamNameMapping";

export default function MatchDayPage() {
  const { user, getAuthHeader } = useAuth();
  const { winnersData, showCelebration, dismissCelebration } = useCelebration("matchday");
  const [todayMatches, setTodayMatches] = useState([]);
  const [upcomingMatches, setUpcomingMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [leaderboard, setLeaderboard] = useState([]);
  const [userStats, setUserStats] = useState(null);

  const fetchData = useCallback(async (signal) => {
    try {
      const headers = user ? { Authorization: `Bearer ${await user.getIdToken()}` } : {};
      const opts = signal ? { headers, signal } : { headers };
      const optsNoAuth = signal ? { signal } : {};
      const [matchesRes, lbRes] = await Promise.all([
        axios.get(`${API}/daily/matches/today`, opts),
        axios.get(`${API}/daily/leaderboard/cumulative?limit=5`, optsNoAuth),
      ]);
      
      if (signal?.aborted) return;
      
      setTodayMatches(matchesRes.data.today || []);
      setUpcomingMatches(matchesRes.data.upcoming || []);
      setLeaderboard(lbRes.data.leaderboard || []);

      if (user && !signal?.aborted) {
        const homeRes = await axios.get(`${API}/home`, opts);
        if (homeRes.data.daily_rank) {
          setUserStats({
            rank: homeRes.data.daily_rank,
            points: homeRes.data.daily_total_points,
            matches: homeRes.data.daily_matches_played,
            currentWeekCredits: homeRes.data.current_week_credits || 0,
          });
        }
      }
    } catch (e) {
      if (e.name !== 'CanceledError') {
        console.error("Failed to fetch daily data:", e);
      }
    } finally {
      if (!signal?.aborted) {
        setLoading(false);
      }
    }
  }, [user]);

  useEffect(() => {
    const abortController = new AbortController();
    fetchData(abortController.signal);
    return () => abortController.abort();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div>
    <div className="max-w-2xl mx-auto px-4 py-6 space-y-6" data-testid="match-day-page">
      {showCelebration && winnersData && (
        <CelebrationOverlay winnersData={winnersData} pageType="matchday" onDismiss={dismissCelebration} />
      )}
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
            <Zap className="w-5 h-5 text-amber-400" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white" data-testid="match-day-title">Match Day</h1>
            <p className="text-sm text-slate-400">Pick your XI for today's match</p>
          </div>
        </div>
        {user && (
          <Link 
            to="/daily/history" 
            className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 border border-slate-700 rounded-lg text-slate-300 hover:bg-slate-700 text-sm transition-colors"
            data-testid="view-history-btn"
          >
            <History size={14} />
            My Teams
          </Link>
        )}
      </div>

      {/* Today's Matches */}
      {todayMatches.length > 0 ? (
        <div className="space-y-3" data-testid="today-matches-section">
          <h2 className="text-sm font-semibold text-amber-400 uppercase tracking-wider">Today's Match{todayMatches.length > 1 ? "es" : ""}</h2>
          {todayMatches.map((match) => (
            <MatchDayCard key={match.match_id} match={match} />
          ))}
        </div>
      ) : (
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 text-center" data-testid="no-today-matches">
          <Calendar className="w-10 h-10 text-slate-500 mx-auto mb-3" />
          <p className="text-slate-400 text-sm">No matches today</p>
          <p className="text-slate-500 text-xs mt-1">Check back tomorrow!</p>
        </div>
      )}

      {/* User Daily Stats */}
      {userStats && (
        <div className="bg-gradient-to-r from-amber-500/10 to-purple-500/10 border border-amber-500/20 rounded-xl p-4" data-testid="daily-stats">
          <h3 className="text-sm font-semibold text-amber-400 mb-3">Your Match Day Stats</h3>
          <div className="grid grid-cols-3 gap-3">
            <div className="text-center">
              <p className="text-xl font-bold text-white">{userStats.points}</p>
              <p className="text-xs text-slate-400">Total Points</p>
            </div>
            <div className="text-center">
              <p className="text-xl font-bold text-cyan-400">#{userStats.rank}</p>
              <p className="text-xs text-slate-400">Rank</p>
            </div>
            <div className="text-center">
              <p className="text-xl font-bold text-emerald-400">{userStats.matches}</p>
              <p className="text-xs text-slate-400">Matches</p>
            </div>
          </div>
          {/* Season Credits */}
          {userStats.currentWeekCredits > 0 && (
            <div className="mt-3 pt-3 border-t border-slate-700/50">
              <p className="text-xs text-purple-400 font-medium">
                Season Credits Earned: {userStats.currentWeekCredits.toFixed(1)} / 5.0 Cr this week
              </p>
            </div>
          )}
        </div>
      )}

      {/* Upcoming Matches */}
      {upcomingMatches.length > 0 && (
        <div className="space-y-3" data-testid="upcoming-matches-section">
          <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider">Upcoming</h2>
          {upcomingMatches.map((match) => (
            <MatchDayCard key={match.match_id} match={match} compact />
          ))}
        </div>
      )}

      {/* Cumulative Leaderboard Preview */}
      {leaderboard.length > 0 && (
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4" data-testid="daily-leaderboard-preview">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-white flex items-center gap-2">
              <Trophy className="w-4 h-4 text-amber-400" /> Match Day Leaderboard
            </h3>
          </div>
          <div className="space-y-2">
            {leaderboard.map((entry) => (
              <div key={entry.user_id} className="flex items-center gap-3 py-1.5">
                <span className="text-xs font-bold text-slate-500 w-5">#{entry.rank}</span>
                <span className="text-sm text-white flex-1 truncate">{entry.team_name || entry.display_name}</span>
                <span className="text-sm font-semibold text-amber-400">{entry.daily_total_points} pts</span>
                <span className="text-xs text-slate-500">{entry.daily_matches_played}m</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
    </div>
  );
}

function MatchDayCard({ match, compact = false }) {
  const isLocked = match.is_locked;
  const hasTeam = match.has_daily_team;
  const matchTime = match.time_gmt || "";
  const isAbandoned = match.status === "abandoned" || match.status === "no_result";

  // Countdown to lock time - formatted as Days/Hours/Minutes/Seconds
  const [countdown, setCountdown] = useState("");
  useEffect(() => {
    if (isLocked || !match.lock_time) return;
    const update = () => {
      const lockDt = new Date(match.lock_time);
      const now = new Date();
      const diff = lockDt - now;
      if (diff <= 0) {
        setCountdown("Locked");
        return;
      }
      const days = Math.floor(diff / 86400000);
      const hrs = Math.floor((diff % 86400000) / 3600000);
      const mins = Math.floor((diff % 3600000) / 60000);
      const secs = Math.floor((diff % 60000) / 1000);
      
      // Format: show days if > 0, always show hours and minutes
      if (days > 0) {
        setCountdown(`${days}d ${hrs}h ${mins}m`);
      } else if (hrs > 0) {
        setCountdown(`${hrs}h ${mins}m ${secs}s`);
      } else {
        setCountdown(`${mins}m ${secs}s`);
      }
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [isLocked, match.lock_time]);

  if (compact) {
    return (
      <Link
        to={`/daily/team/${match.match_id}`}
        className="flex flex-col bg-slate-800/30 border border-slate-700/50 rounded-lg p-3 hover:border-amber-500/30 transition-colors"
        data-testid={`upcoming-match-${match.match_id}`}
      >
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <span className="text-sm font-semibold text-white">{getDisplayShortName(match.team1_short) || getDisplayShortName(match.team1) || match.team1_short || match.team1} vs {getDisplayShortName(match.team2_short) || getDisplayShortName(match.team2) || match.team2_short || match.team2}</span>
            <p className="text-xs text-slate-500 mt-0.5">{match.date} • {matchTime} GMT</p>
          </div>
          <ChevronRight className="w-4 h-4 text-slate-500" />
        </div>
        {match.sponsor && match.sponsor.name && (
          <div className="mt-2 pt-2 border-t border-slate-700/50">
            <SponsorBanner sponsor={match.sponsor} variant="header" />
          </div>
        )}
      </Link>
    );
  }

  return (
    <div
      className={`rounded-xl border p-4 ${isLocked ? "bg-slate-800/50 border-slate-700" : "bg-amber-500/5 border-amber-500/20"}`}
      data-testid={`today-match-${match.match_id}`}
    >
      {/* Sponsor banner */}
      {match.sponsor && <div className="mb-2"><SponsorBanner sponsor={match.sponsor} /></div>}

      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-lg font-bold text-white">{getDisplayShortName(match.team1_short) || getDisplayShortName(match.team1) || match.team1_short || match.team1} vs {getDisplayShortName(match.team2_short) || getDisplayShortName(match.team2) || match.team2_short || match.team2}</h3>
          <p className="text-xs text-slate-400 mt-0.5">{match.venue || match.date} • {matchTime} GMT</p>
        </div>
        {isAbandoned ? (
          <span className="flex items-center gap-1 text-xs font-semibold text-orange-400 bg-orange-500/10 px-2.5 py-1 rounded-full" data-testid={`abandoned-badge-${match.match_id}`}>
            {match.status === "abandoned" ? "Abandoned" : "No Result"}
          </span>
        ) : isLocked ? (
          <span className="flex items-center gap-1 text-xs font-semibold text-red-400 bg-red-500/10 px-2.5 py-1 rounded-full">
            <Lock className="w-3 h-3" /> Locked
          </span>
        ) : (
          <span className="flex items-center gap-1 text-xs font-semibold text-amber-400 bg-amber-500/10 px-2.5 py-1 rounded-full">
            <Clock className="w-3 h-3" /> {countdown}
          </span>
        )}
      </div>

      {/* CTA */}
      {isAbandoned ? (
        <div className="text-center py-2.5 rounded-lg bg-orange-500/10 border border-orange-500/20 text-orange-400 text-sm" data-testid={`abandoned-notice-${match.match_id}`}>
          This match was {match.status === "abandoned" ? "abandoned" : "declared no result"}. No points were awarded.
        </div>
      ) : isLocked ? (
        hasTeam ? (
          <Link
            to={`/daily/team/${match.match_id}`}
            className="block w-full text-center py-2.5 rounded-lg bg-slate-700/50 text-slate-300 text-sm font-medium"
            data-testid={`view-locked-team-${match.match_id}`}
          >
            <Shield className="w-4 h-4 inline mr-1.5" />View Your Team
          </Link>
        ) : (
          <div className="text-center py-2.5 rounded-lg bg-slate-700/30 text-slate-500 text-sm" data-testid={`locked-no-team-${match.match_id}`}>
            Team locked — match in progress
          </div>
        )
      ) : hasTeam ? (
        <Link
          to={`/daily/team/${match.match_id}`}
          className="block w-full text-center py-2.5 rounded-lg bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 text-sm font-semibold hover:bg-emerald-500/25 transition-colors"
          data-testid={`edit-team-${match.match_id}`}
        >
          <Check className="w-4 h-4 inline mr-1.5" />Team Ready — View / Edit
        </Link>
      ) : (
        <Link
          to={`/daily/team/${match.match_id}`}
          className="block w-full text-center py-2.5 rounded-lg bg-amber-500/15 border border-amber-500/30 text-amber-400 text-sm font-bold hover:bg-amber-500/25 transition-colors"
          data-testid={`build-team-${match.match_id}`}
        >
          Build Your Team <ChevronRight className="w-4 h-4 inline" />
        </Link>
      )}
    </div>
  );
}
